import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge
} from '@chakra-ui/react';
import { FaCheckCircle } from 'react-icons/fa';
import MarketingFooter from './MarketingFooter';
import { marketingHeadingStyles } from './marketingStyles';

const PaymentsDisclosurePage = () => {
  return (
    <Box fontFamily="var(--telivi-font-body)" sx={marketingHeadingStyles}>
      <Box bgGradient="linear(to-b, gray.50, white)" pt={{ base: 16, lg: 24 }} pb={{ base: 12, lg: 16 }}>
        <Container maxW="container.xl">
          <Stack spacing={4} textAlign="center">
            <Text color="blue.500" fontWeight="600" textTransform="uppercase">
              Payments Disclosure
            </Text>
            <Heading as="h1" fontSize={{ base: '3xl', md: '5xl' }}>
              Clear, transparent payment responsibilities
            </Heading>
            <Text color="gray.600" fontSize="lg">
              This page explains how billing and payments work on the Telivi platform, including
              who is responsible for collecting payments, how fees are applied, and how Stripe is
              used.
            </Text>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 10, md: 12 }}>
        <Container maxW="container.xl">
          <Stack spacing={6}>
            <Text color="blue.500" fontWeight="600" textTransform="uppercase">
              At a glance
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" p={6} boxShadow="sm">
                <Badge colorScheme="blue" mb={3}>
                  Platform fees
                </Badge>
                <Heading as="h3" fontSize="lg" mb={2}>
                  Telivi bills only platform fees
                </Heading>
                <Text color="gray.600">
                  Telivi provides payment and billing infrastructure only. Customers retain full
                  control and ownership of their revenue.
                </Text>
              </Box>
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" p={6} boxShadow="sm">
                <Badge colorScheme="green" mb={3}>
                  Merchant of Record
                </Badge>
                <Heading as="h3" fontSize="lg" mb={2}>
                  You are the MoR for your customers
                </Heading>
                <Text color="gray.600">
                  You collect customer payments, handle taxes, issue refunds, and manage disputes
                  through your own Stripe account.
                </Text>
              </Box>
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" p={6} boxShadow="sm">
                <Badge colorScheme="purple" mb={3}>
                  Stripe processing
                </Badge>
                <Heading as="h3" fontSize="lg" mb={2}>
                  Stripe handles payment flow and payouts
                </Heading>
                <Text color="gray.600">
                  Funds flow from your customers to your Stripe account and are deposited into your
                  bank account by Stripe.
                </Text>
              </Box>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 12, md: 16 }} bg="gray.50">
        <Container maxW="container.xl">
          <Stack spacing={8}>
            <Stack spacing={4} textAlign="center">
              <Text color="blue.500" fontWeight="600" textTransform="uppercase">
                Merchant of Record (MoR)
              </Text>
              <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>
                Who is responsible for customer payments
              </Heading>
              <Text color="gray.600">
                The Merchant of Record is the legal entity responsible for selling to customers,
                collecting payment, handling taxes, issuing refunds, and managing disputes.
              </Text>
            </Stack>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100">
                <Heading as="h3" fontSize="lg" mb={2}>
                  Telivi IT LLC
                </Heading>
                <Text fontWeight="600" color="gray.700" mb={2}>
                  Merchant of Record only for:
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Telivi platform subscription fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Telivi setup fees
                  </ListItem>
                </List>
              </Box>
              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100">
                <Heading as="h3" fontSize="lg" mb={2}>
                  You (the Customer / Tenant)
                </Heading>
                <Text fontWeight="600" color="gray.700" mb={2}>
                  Merchant of Record for:
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    All payments from your customers
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Invoices and subscriptions you create
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Taxes, refunds, disputes, and chargebacks
                  </ListItem>
                </List>
              </Box>
            </SimpleGrid>
            <Box bg="white" borderRadius="xl" p={5} border="1px solid" borderColor="gray.100">
              <Text fontWeight="600" color="gray.700">
                Your business name appears on your customers&apos; payment statements - not Telivi&apos;s.
              </Text>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 12, md: 16 }}>
        <Container maxW="container.xl">
          <Stack spacing={10}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100" boxShadow="sm">
                <Badge colorScheme="blue" mb={3}>
                  Telivi platform fees
                </Badge>
                <Heading as="h2" fontSize="xl" mb={3}>
                  Subscription and setup fees
                </Heading>
                <Text color="gray.600" mb={4}>
                  Telivi charges customers for access to the Telivi platform, infrastructure, and
                  dashboards.
                </Text>
                <Text fontWeight="600" color="gray.700" mb={2}>
                  These fees may include:
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Recurring subscription fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    One-time setup fees (based on selected tier)
                  </ListItem>
                </List>
                <Divider my={4} />
                <Text fontWeight="600" color="gray.700" mb={2}>
                  Key points
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Billed directly by Telivi IT LLC
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Separate from any customer payments you collect
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    No revenue sharing or transaction-based fees
                  </ListItem>
                </List>
              </Box>

              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100" boxShadow="sm">
                <Badge colorScheme="purple" mb={3}>
                  Customer payments
                </Badge>
                <Heading as="h2" fontSize="xl" mb={3}>
                  Payments flow through your Stripe account
                </Heading>
                <Text color="gray.600" mb={4}>
                  When using Payments as a Service, you connect your own Stripe account.
                </Text>
                <Text fontWeight="600" color="gray.700" mb={2}>
                  How this works
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    You create invoices or subscriptions using Telivi dashboards
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Your customers pay you directly through Stripe
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Funds are deposited into your bank account by Stripe
                  </ListItem>
                </List>
                <Divider my={4} />
                <Text fontWeight="600" color="gray.700" mb={2}>
                  Telivi:
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Does not collect customer payments
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Does not hold funds
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Does not route payouts
                  </ListItem>
                </List>
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100" boxShadow="sm">
                <Badge colorScheme="orange" mb={3}>
                  Stripe fees
                </Badge>
                <Heading as="h2" fontSize="xl" mb={3}>
                  Stripe processing fees (transparent)
                </Heading>
                <Text color="gray.600" mb={4}>
                  Stripe charges processing fees directly to your connected Stripe account,
                  according to Stripe&apos;s published pricing.
                </Text>
                <Text fontWeight="600" color="gray.700" mb={2}>
                  These fees may include:
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Standard card processing fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    International or cross-border card fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Currency conversion fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Subscription billing fees (if applicable)
                  </ListItem>
                </List>
                <Divider my={4} />
                <Text fontWeight="600" color="gray.700" mb={2}>
                  Important
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Telivi does not add markups to Stripe fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Telivi does not receive any portion of Stripe processing fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Stripe fees may change over time per Stripe&apos;s terms
                  </ListItem>
                </List>
              </Box>

              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100" boxShadow="sm">
                <Badge colorScheme="gray" mb={3}>
                  Refunds
                </Badge>
                <Heading as="h2" fontSize="xl" mb={3}>
                  Refunds are handled through your Stripe account
                </Heading>
                <Text color="gray.600" mb={4}>
                  Refunds are handled directly through your Stripe account.
                </Text>
                <Text fontWeight="600" color="gray.700" mb={2}>
                  Please note:
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Stripe processing fees are generally not refunded
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Card networks and payment processors charge fees regardless of refund status
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Telivi does not issue refunds to your customers
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Refund policies are fully controlled by you
                  </ListItem>
                </List>
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100" boxShadow="sm">
                <Badge colorScheme="red" mb={3}>
                  Disputes
                </Badge>
                <Heading as="h2" fontSize="xl" mb={3}>
                  Disputes and chargebacks
                </Heading>
                <Text color="gray.600" mb={4}>
                  If a customer initiates a dispute or chargeback:
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    The dispute is handled through your Stripe account
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Stripe may charge dispute or chargeback fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    These fees are charged directly by Stripe
                  </ListItem>
                </List>
                <Divider my={4} />
                <Text fontWeight="600" color="gray.700" mb={2}>
                  Telivi:
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Does not intervene in disputes
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Does not assume liability for chargebacks
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Does not manage dispute outcomes
                  </ListItem>
                </List>
                <Text color="gray.600" mt={4}>
                  You remain responsible as the Merchant of Record.
                </Text>
              </Box>

              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100" boxShadow="sm">
                <Badge colorScheme="teal" mb={3}>
                  Payouts
                </Badge>
                <Heading as="h2" fontSize="xl" mb={3}>
                  Payouts go directly to your bank
                </Heading>
                <Text color="gray.600" mb={4}>
                  Stripe deposits funds directly into your bank account based on your Stripe payout
                  settings.
                </Text>
                <List spacing={2} color="gray.600">
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Payout timing and frequency are controlled by Stripe
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Payout fees (if any) are Stripe fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Telivi does not delay, batch, or redirect payouts
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Telivi never holds your funds
                  </ListItem>
                </List>
              </Box>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 12, md: 16 }} bg="gray.50">
        <Container maxW="container.xl">
          <Stack spacing={10}>
            <Box bg="white" borderRadius="2xl" p={{ base: 6, md: 8 }} border="1px solid" borderColor="gray.100">
              <Text color="blue.500" fontWeight="600" textTransform="uppercase" mb={2}>
                Important clarifications
              </Text>
              <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} mb={4}>
                Telivi provides software and infrastructure only
              </Heading>
              <List spacing={3} color="gray.600">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="green.500" />
                  Telivi is not a payment processor
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="green.500" />
                  Telivi is not a marketplace
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="green.500" />
                  Telivi does not take a percentage of customer revenue
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="green.500" />
                  Telivi does not resell your services
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="green.500" />
                  Telivi does not manage customer funds
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="green.500" />
                  Telivi provides software and infrastructure only
                </ListItem>
              </List>
            </Box>

            <Box bg="white" borderRadius="2xl" p={{ base: 6, md: 8 }} border="1px solid" borderColor="gray.100">
              <Text color="blue.500" fontWeight="600" textTransform="uppercase" mb={2}>
                Acknowledgement
              </Text>
              <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} mb={4}>
                Required before enabling payments
              </Heading>
              <Text color="gray.600" mb={4}>
                Before enabling Payments as a Service, customers are required to acknowledge the
                following:
              </Text>
              <Box bg="gray.50" borderRadius="xl" p={5} border="1px solid" borderColor="gray.200">
                <Text fontStyle="italic" color="gray.700">
                  "I understand that Telivi bills me only for platform subscription and setup fees,
                  and that my customers pay me directly through my connected Stripe account. I
                  remain the Merchant of Record for all customer transactions."
                </Text>
              </Box>
              <Text color="gray.600" mt={3}>
                This acknowledgement is required to activate payment features.
              </Text>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 12, md: 16 }}>
        <Container maxW="container.xl">
          <Stack spacing={8}>
            <Stack spacing={3} textAlign="center">
              <Text color="blue.500" fontWeight="600" textTransform="uppercase">
                Example payment flow
              </Text>
              <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>
                Illustrative flow for a $100 invoice
              </Heading>
            </Stack>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100">
                <Text fontWeight="600" color="gray.700" mb={2}>
                  Scenario
                </Text>
                <Text color="gray.600" mb={4}>
                  A customer uses Telivi to invoice their own client for $100.
                </Text>
                <Text fontWeight="600" color="gray.700" mb={2}>
                  Payment flow
                </Text>
                <List spacing={2} color="gray.600" mb={6}>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Tenant creates invoice in Telivi dashboard
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Tenant&apos;s customer pays $100 via Stripe
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Stripe applies processing fees
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Stripe deposits funds into tenant&apos;s bank account
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} color="green.500" />
                    Telivi separately bills tenant for platform subscription (monthly)
                  </ListItem>
                </List>
                <Heading as="h3" fontSize="lg" mb={3}>
                  Summary table
                </Heading>
                <Box border="1px solid" borderColor="gray.100" borderRadius="lg" overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Item</Th>
                        <Th>Amount</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td>Customer payment</Td>
                        <Td>$100</Td>
                      </Tr>
                      <Tr>
                        <Td>Stripe processing fees</Td>
                        <Td>Charged by Stripe</Td>
                      </Tr>
                      <Tr>
                        <Td>Net payout</Td>
                        <Td>Deposited to tenant</Td>
                      </Tr>
                      <Tr>
                        <Td>Telivi involvement</Td>
                        <Td>None</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
              </Box>

              <Box bg="white" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.100">
                <Heading as="h3" fontSize="lg" mb={4}>
                  Flow diagram
                </Heading>
                <Box bg="gray.50" borderRadius="xl" p={5} fontFamily="mono" fontSize="sm" color="gray.700">
                  <Text>Customer -> Stripe -> Tenant Bank Account</Text>
                  <Text pl={4}>|</Text>
                  <Text pl={4}>Stripe fees (direct)</Text>
                  <Divider my={4} />
                  <Text>Telivi -> Subscription Invoice -> Tenant</Text>
                </Box>
              </Box>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 12, md: 16 }}>
        <Container maxW="container.md">
          <Box bgGradient="linear(to-r, blue.50, orange.50)" borderRadius="2xl" p={{ base: 6, md: 8 }}>
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} mb={3}>
              Final statement
            </Heading>
            <Text color="gray.700" fontSize="lg">
              Telivi is designed to give customers full ownership of their revenue while providing
              powerful billing and payment tools. You control your Stripe account. You control your
              customers. You control your business.
            </Text>
            <Text color="blue.700" fontWeight="600" mt={4}>
              Your revenue stays yours. Telivi powers the platform - not the payments.
            </Text>
          </Box>
        </Container>
      </Box>

      <MarketingFooter />
    </Box>
  );
};

export default PaymentsDisclosurePage;
